import { useMemo, type ReactNode } from "react"
import { Outlet, useMatches } from "@tanstack/react-router"
import { ColumnLayout } from "@/components/layout/ColumnLayout"
import type { ColumnSpec } from "@/lib/column-layout"

export function parseRouteFocus(value: unknown): number | undefined {
  const raw = typeof value === "number" ? value : Number(value)
  return Number.isInteger(raw) && raw >= 1 ? raw : undefined
}

export function useRouteRecordSelection<T extends string>(
  ...keys: T[]
): Record<T, string | null> {
  const matches = useMatches()
  const keySig = keys.join("\0")

  return useMemo(() => {
    const selected = Object.fromEntries(keys.map((key) => [key, null])) as Record<T, string | null>
    for (const match of matches) {
      const params = match.params as Partial<Record<T, string>>
      for (const key of keys) {
        if (params?.[key]) selected[key] = params[key] ?? null
      }
    }
    return selected
    // `keys` is usually a short literal rest list; keySig gives it stable deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, keySig])
}

export function RoutedCollectionColumns({
  columns,
  focus,
}: {
  columns: ColumnSpec[]
  focus?: number
}) {
  return (
    <>
      <ColumnLayout columns={columns} focus={focus} />
      <Outlet />
    </>
  )
}

export function RoutedCollectionLayout({
  columns,
  focus,
  className = "relative flex h-full min-w-0",
  children,
}: {
  columns: ColumnSpec[]
  focus?: number
  className?: string
  children?: ReactNode
}) {
  return (
    <div className={className}>
      <RoutedCollectionColumns columns={columns} focus={focus} />
      {children}
    </div>
  )
}
